import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RETURN_ON_INVESTMENT_STATS } from '../dashboardStatsConfig';
import { parseStatValue } from '../dashboardStatUtils';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(amount);

const formatCount = (count) =>
    new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    }).format(count);

function DashboardReturnOnInvestment({ getStat, isLoading, loadError, lastUpdated }) {
    const roiRows = useMemo(
        () =>
            RETURN_ON_INVESTMENT_STATS.map((stat) => {
                const count = parseStatValue(getStat(stat.key));
                return {
                    ...stat,
                    count,
                    amount: count * stat.dollarValue,
                };
            }),
        [getStat]
    );

    const totalAmount = useMemo(
        () => roiRows.reduce((sum, row) => sum + row.amount, 0),
        [roiRows]
    );

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--rose">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-graph-up-arrow" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Return on Investment</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `Estimated value · Last 30 days · Updated ${lastUpdated}`
                                    : 'Estimated value from appointments, feedback, and comments — last 30 days'}
                            </p>
                        </div>
                    </div>
                    <Link to="/reputation" className="btn btn-sm btn-light-primary rounded-pill">
                        Open reputation
                        <i className="bi bi-arrow-right ms-1" />
                    </Link>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-roi" aria-hidden="true">
                        <div className="dashboard-roi__summary dashboard-stat-tile--skeleton" style={{ minHeight: '4.5rem' }} />
                        <div className="dashboard-roi__table">
                            {Array.from({ length: 3 }).map((_, index) => (
                                <div
                                    key={`roi-row-skel-${index}`}
                                    className="dashboard-roi__row dashboard-stat-tile--skeleton"
                                    style={{ minHeight: '3rem', border: 0 }}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dashboard-roi">
                        <div className="dashboard-roi__summary">
                            <div className="dashboard-roi__summary-copy">
                                <span className="dashboard-roi__summary-label">Estimated total value</span>
                                <span className="dashboard-roi__summary-hint">
                                    Calculated from confirmed appointments ($15), feedback ($10), and comments ($20)
                                </span>
                            </div>
                            <span className="dashboard-roi__summary-value">{formatCurrency(totalAmount)}</span>
                        </div>

                        <div className="dashboard-roi__table" role="table" aria-label="ROI breakdown">
                            <div className="dashboard-roi__head" role="row">
                                <span role="columnheader">Metric</span>
                                <span role="columnheader">Calculation</span>
                                <span role="columnheader">Value</span>
                            </div>

                            {roiRows.map((stat) => (
                                <div key={stat.key} className="dashboard-roi__row" role="row">
                                    <div className="dashboard-roi__metric" role="cell">
                                        <span className="dashboard-roi__metric-icon" aria-hidden="true">
                                            <i className={`bi ${stat.icon}`} />
                                        </span>
                                        <span className="dashboard-roi__metric-label">{stat.label}</span>
                                    </div>
                                    <span className="dashboard-roi__calc" role="cell">
                                        {formatCount(stat.count)}
                                        <span className="dashboard-roi__calc-sep">×</span>
                                        {formatCurrency(stat.dollarValue)}
                                    </span>
                                    <span className="dashboard-roi__amount" role="cell">
                                        {formatCurrency(stat.amount)}
                                    </span>
                                </div>
                            ))}

                            <div className="dashboard-roi__row dashboard-roi__row--total" role="row">
                                <span className="dashboard-roi__metric-label" role="cell">
                                    Total
                                </span>
                                <span className="dashboard-roi__calc" role="cell" aria-hidden="true" />
                                <span className="dashboard-roi__amount" role="cell">
                                    {formatCurrency(totalAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default DashboardReturnOnInvestment;
